/**
 * @file Handles all configurations for the AHD Fleet.
 * @author Phillip Kühn
 */
import isUndefinedNullOrEmpty from '@ahd/shared/tools/is-undefined-null-or-empty';

import { getStorage } from '../hooks/use-session-storage/utils';
import merge from '../utils/tools/merge';
import { LOCAL_STORAGE_SETTINGS, PORT } from '../vars';
import configAhdWeb from './_ahd-web';
import configDefault from './_defaultValues';
import { mapConfigs } from './mapConfigs';
import { type ConfigHostsEntry, mapHosts } from './mapHosts';
import { type ConfigObject, DebugLevel } from './types';

import type { FleetSessionStorageSettings } from '../types';
import type { ConfigName } from './names';

function checkHosts(entry: ConfigHostsEntry, locHost: string): boolean {
    if (!isUndefinedNullOrEmpty(entry?.hosts)) {
        // Try direct matching
        if (!isUndefinedNullOrEmpty(entry?.hosts?.find((h) => h === locHost))) {
            return true;
        }

        // Try variants of non "autohaus-digital.de" hosts
        const nonAhdHosts = entry?.hosts?.filter((h) => !h?.endsWith('.autohaus-digital.de'))?.map((h) => h?.replace(/\.\w{2,}$/, '')?.replace('.', '-'));
        const variants = nonAhdHosts?.reduce(
            (p, c) => [
                ...p,
                ...(c === 'localhost'
                    ? [`localhost:${PORT}`]
                    : [`${c}-dev.autohaus-digital.de`, `${c}-next.autohaus-digital.de`, `${c}-live.autohaus-digital.de`, `${c}-pre-release.autohaus-digital.de`, `${c}-test.autohaus-digital.de`]),
            ],
            []
        );
        if (!isUndefinedNullOrEmpty(variants?.find((h) => h === locHost))) {
            return true;
        }
    }

    return false;
}

/** Function to get configuration according to the location. */
async function sConfigs(locHost: string | undefined, additionalConfig: Partial<ConfigObject> = {}): Promise<ConfigObject> {
    const locationHost = locHost || (location.pathname.startsWith('/install') ? location.search.replace('?host=', '') : location.host);
    const configKeys = Object.entries(mapHosts) as [ConfigName, ConfigHostsEntry][];
    const configKey = configKeys?.find((c) => {
        const key = c?.[0];
        return checkHosts(mapHosts?.[key], locationHost?.replace('www.', '')?.replace(/:[0-9]{1,5}/, ''));
    });
    const config = await mapConfigs(configKey?.[0]);

    const sessionStorage = getStorage<FleetSessionStorageSettings>(LOCAL_STORAGE_SETTINGS);

    const configObject: ConfigObject = merge({}, configDefault, config, config?.general?.ahdWeb === true ? configAhdWeb : {}, additionalConfig, {
        general: { debug: sessionStorage?.debug || config?.general?.debug || DebugLevel.NONE },
    });

    return configObject;
}

export default sConfigs;
